import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellRingIcon, CameraIcon, ClockIcon, MapPinIcon, XIcon } from 'lucide-react';
import type { Pet } from '../../data/animaux';
interface LostFlowProps {
  pet: Pet;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}
type Step = 'lieu' | 'moment' | 'photo' | 'zone' | 'revue';
const places = ['Parc Maupassant', '4590, rue Bourgogne', 'Position actuelle'];
const moments = ['Aujourd’hui · 15 h 20', 'Aujourd’hui · matin', 'Hier soir'];
export function LostFlow({
  pet,
  open,
  onClose,
  onConfirm
}: LostFlowProps) {
  const [step, setStep] = useState<Step>('lieu');
  const [place, setPlace] = useState(places[0]);
  const [moment, setMoment] = useState(moments[0]);
  const [radius, setRadius] = useState(1.5);
  const choiceClass = (active: boolean) => `rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft text-ville-indigo' : 'border-slate-200 text-ville-navy hover:border-ville-indigo'}`;
  return <AnimatePresence data-id="element-142" data-mp="fd325jawxmm0">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-143" data-mp="j883jff21uv">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-144" data-mp="y6gq34g7kss4" />

          <motion.div role="dialog" aria-modal="true" aria-label={`${pet.name} est perdu`} initial={{
        y: 24,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 16,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl sm:p-8" data-id="element-145" data-mp="jfzkd8wjjgpf">
            <div className="flex items-start justify-between gap-4" data-id="element-146" data-mp="1ludg326qj2i1">
              <div data-id="element-147" data-mp="1hr7fob6b05jh">
                <h2 className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-148" data-mp="1hqnxctcqrf4x">{pet.name} est perdu</h2>
                <p className="mt-1 text-sm text-slate-600" data-id="element-149" data-mp="1cruj2ypmrk3p">
                  Nous allons vous aider à informer les personnes à proximité.
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Fermer" className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-150" data-mp="132ko9b3ejnbw">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-151" data-mp="k3f76l1sphwl" />
              </button>
            </div>

            <div className="mt-6" data-id="element-152" data-mp="2vs3iti0xbnk5">
              {step === 'lieu' && <div data-id="element-153" data-mp="30qeqpn7nrnzq">
                  <p className="flex items-center gap-2 text-[15px] font-bold text-ville-navy" data-id="element-154" data-mp="30plu3f2eyevb">
                    <MapPinIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-155" data-mp="35oycnnm94zs3" />
                    Où {pet.name} a-t-il été vu pour la dernière fois ?
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-156" data-mp="2d12mz2v3o84t">
                    {places.map(option => <li key={option} data-id="element-157" data-mp="2hzdwfchhggmi">
                        <button type="button" onClick={() => setPlace(option)} className={choiceClass(place === option)} data-id="element-158" data-mp="2i0f4d837cn00">
                          {option}
                        </button>
                      </li>)}
                  </ul>
                </div>}

              {step === 'moment' && <div data-id="element-159" data-mp="1ybwmlbkj0bvc">
                  <p className="flex items-center gap-2 text-[15px] font-bold text-ville-navy" data-id="element-160" data-mp="1ybem22qtzbhm">
                    <ClockIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-161" data-mp="1f6rvfv91f8ic" />
                    Quand ?
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2" data-id="element-162" data-mp="1a9rprlg3o7rr">
                    {moments.map(option => <li key={option} data-id="element-163" data-mp="1oyfjrnaas9fl">
                        <button type="button" onClick={() => setMoment(option)} className={choiceClass(moment === option)} data-id="element-164" data-mp="1k5upkknhbubj">
                          {option}
                        </button>
                      </li>)}
                  </ul>
                </div>}

              {step === 'photo' && <div data-id="element-165" data-mp="27vgn73o7e1hf">
                  <p className="flex items-center gap-2 text-[15px] font-bold text-ville-navy" data-id="element-166" data-mp="27tw367zim0xt">
                    <CameraIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-167" data-mp="22usxclbpnqos" />
                    Photo récente
                  </p>
                  <div className="mt-4 flex items-center gap-4 rounded-2xl border border-ville-indigo bg-ville-indigo-soft p-4" data-id="element-168" data-mp="8jeaqddzy7d9">
                    <img src={pet.photo} alt={pet.name} className="h-20 w-20 rounded-2xl object-cover" data-id="element-169" data-mp="2k6q3j9b75tck" />
                    <p className="text-sm text-ville-navy" data-id="element-170" data-mp="2p4ro5yo9u0q5">Photo du profil de {pet.name}</p>
                  </div>
                  <button type="button" className="mt-3 text-sm font-semibold text-slate-500 underline underline-offset-4 hover:text-ville-navy" data-id="element-171" data-mp="2aeamce7q58sq">
                    Choisir une autre photo
                  </button>
                </div>}

              {step === 'zone' && <div data-id="element-172" data-mp="1ric5wuo0sz1j">
                  <p className="text-[15px] font-bold text-ville-navy" data-id="element-173" data-mp="1riu6g3hptzf9">Zone de recherche</p>
                  <div className="relative mt-4 h-44 overflow-hidden rounded-2xl bg-slate-100" data-id="element-174" data-mp="1wheyvgqq7et5">
                    <div className="absolute inset-0 opacity-60 [background-image:linear-gradient(#cbd5e1_1px,transparent_1px),linear-gradient(90deg,#cbd5e1_1px,transparent_1px)] [background-size:28px_28px]" data-id="element-175" data-mp="3lwlcyu4r036h" />
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ville-indigo bg-ville-indigo/10" style={{
                width: `${60 + radius * 40}px`,
                height: `${60 + radius * 40}px`
              }} data-id="element-176" data-mp="3pf2fg0ruu92f" />
                    <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ville-indigo" data-id="element-177" data-mp="3ahrum0tbnqu1" />
                  </div>
                  <p className="mt-3 text-sm text-slate-600" data-id="element-178" data-mp="6yykjxzr5k4m">
                    <span className="font-bold text-ville-navy" data-id="element-179" data-mp="71l0om0pl7yg">Dernier endroit connu :</span> {place}
                  </p>
                  <label htmlFor="rayon" className="mt-4 block text-sm font-semibold text-slate-600" data-id="element-180" data-mp="3tfck9a3wutfp">
                    Rayon de diffusion : {radius.toFixed(1)} km
                  </label>
                  <input id="rayon" type="range" min={0.5} max={3} step={0.5} value={radius} onChange={event => setRadius(Number(event.target.value))} className="mt-2 w-full accent-[#8a6212]" data-id="element-181" data-mp="pyci0pwoxczv" />
                  <p className="mt-2 text-xs text-slate-500" data-id="element-182" data-mp="176di6nyitpy9">
                    Cette zone aide à cibler les personnes qui pourraient avoir vu {pet.name}. Elle n’indique pas sa
                    position actuelle.
                  </p>
                </div>}

              {step === 'revue' && <div data-id="element-183" data-mp="1l53e409ltjl1">
                  <p className="text-sm font-bold uppercase tracking-wider text-ville-indigo" data-id="element-184" data-mp="1pyfwhsnowu5c">L’alerte est prête</p>
                  <div className="mt-4 flex items-center gap-4 rounded-2xl border border-slate-200 p-4" data-id="element-185" data-mp="1uxiuwu96024c">
                    <img src={pet.photo} alt="" className="h-16 w-16 rounded-xl object-cover" data-id="element-186" data-mp="1zxf02wtllzlh" />
                    <div data-id="element-187" data-mp="24wi5iidhq7tz">
                      <p className="font-extrabold text-ville-navy" data-id="element-188" data-mp="24vypj77ehtil">{pet.name}</p>
                      <p className="text-sm text-slate-500" data-id="element-189" data-mp="2q52jxf3i09ph">
                        {pet.breed} · {place}
                      </p>
                      <p className="text-sm text-slate-500" data-id="element-190" data-mp="2l9et6v9db8hv">{moment}</p>
                    </div>
                  </div>
                  <p className="mt-4 flex items-start gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600" data-id="element-191" data-mp="2u7z59dkfajx4">
                    <BellRingIcon className="mt-0.5 h-4 w-4 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-192" data-mp="26hwincza265w" />
                    Un avis discret sera envoyé par notification web et mobile aux citoyens admissibles dans un rayon de{' '}
                    {radius.toFixed(1)} km. Vos coordonnées personnelles ne sont jamais partagées.
                  </p>
                </div>}
            </div>

            <div className="mt-8 flex items-center justify-between gap-4" data-id="element-193" data-mp="1nr50blxbb5fd">
              <button type="button" onClick={() => {
            const order: Step[] = ['lieu', 'moment', 'photo', 'zone', 'revue'];
            const index = order.indexOf(step);
            if (index === 0) onClose();else setStep(order[index - 1]);
          }} className="text-sm font-semibold text-slate-500 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-194" data-mp="1it3guzvz543d">
                {step === 'lieu' ? 'Annuler' : 'Modifier'}
              </button>
              <button type="button" onClick={() => {
            const order: Step[] = ['lieu', 'moment', 'photo', 'zone', 'revue'];
            const index = order.indexOf(step);
            if (step === 'revue') {
              onConfirm();
              setStep('lieu');
              return;
            }
            setStep(order[index + 1]);
          }} className={`rounded-full px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${step === 'revue' ? 'bg-red-600 focus-visible:ring-red-600' : 'bg-ville-navy focus-visible:ring-ville-indigo'}`} data-id="element-195" data-mp="m0dhfsegifc0">
                {step === 'revue' ? `Déclarer ${pet.name} perdu` : 'Continuer'}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}